// Advanced Deferred Loading Strategy for Shopify
// This script handles aggressive resource optimization:
// 1. Preconnect to third-party domains and resource hints
// 2. Intelligent loading of non-critical CSS
// 3. Dynamic script loading based on visibility
// 4. Third-party script control and throttling

// Initialize performance metrics for monitoring
const perfMetrics = {
  resourcesDeferred: 0,
  resourcesLoaded: 0,
  thirdPartyScriptsDeferred: 0,
  mainThreadTimeBlocked: 0
};

// Add critical preconnect links for performance
function addPreconnects() {
  const preconnectDomains = [
    'https://cdn.shopify.com',
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com'
  ];
  
  preconnectDomains.forEach(domain => {
    if (!document.querySelector(`link[rel="preconnect"][href="${domain}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  });
}

// Load non-critical CSS efficiently
function loadDeferredStyles() {
  // Create a list of non-critical stylesheets
  const deferredStyles = [
    'component-predictive-search.css',
    'component-price.css',
    'component-rte.css',
    'component-search.css',
    'component-slider.css',
    'component-rating.css',
    'component-loading-overlay.css',
    'component-cart-notification.css',
    'component-cart-items.css',
    'component-cart-drawer.css',
    'component-disclosures.css',
    'component-facets.css',
    'component-card.css'
  ];
  
  // Use requestIdleCallback to load CSS during browser idle time
  const loadStylesIdleCallback = (deadline) => {
    // Create a stylesheet batch (requestIdleCallback compatibility check)
    const loadStyleBatch = (startIndex, batchSize) => {
      const endIndex = Math.min(startIndex + batchSize, deferredStyles.length);
      for (let i = startIndex; i < endIndex; i++) {
        const style = deferredStyles[i];
        const linkElement = document.createElement('link');
        linkElement.rel = 'stylesheet';
        linkElement.href = `${window.Shopify?.routes?.root || '/'}cdn/shop/t/168/assets/${style}`;
        linkElement.media = 'print';
        linkElement.onload = function() {
          this.media = 'all';
          perfMetrics.resourcesLoaded++;
        };
        document.head.appendChild(linkElement);
        perfMetrics.resourcesDeferred++;
      }
      
      // If more styles need to be loaded, schedule next batch
      if (endIndex < deferredStyles.length) {
        const nextBatchCallback = (deadline) => {
          if (deadline.timeRemaining() > 5) {
            loadStyleBatch(endIndex, 3);
          } else {
            // Not enough time, reschedule
            requestIdleCallback(nextBatchCallback);
          }
        };
        
        requestIdleCallback(nextBatchCallback);
      }
    };
    
    // Start loading first batch
    if (deadline.timeRemaining() > 10) {
      loadStyleBatch(0, 5);
    } else {
      // Not enough time, reschedule
      requestIdleCallback(loadStylesIdleCallback);
    }
  };
  
  // Begin loading styles with browser idle time or fallback
  if ('requestIdleCallback' in window) {
    requestIdleCallback(loadStylesIdleCallback);
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      deferredStyles.forEach(style => {
        const linkElement = document.createElement('link');
        linkElement.rel = 'stylesheet';
        linkElement.href = `${window.Shopify?.routes?.root || '/'}cdn/shop/t/168/assets/${style}`;
        linkElement.media = 'print';
        linkElement.onload = function() {
          this.media = 'all';
          perfMetrics.resourcesLoaded++;
        };
        document.head.appendChild(linkElement);
        perfMetrics.resourcesDeferred++;
      });
    }, 1000);
  }
}

// Implement intersection observer for efficient script loading
function setupIntersectionObserver() {
  if ('IntersectionObserver' in window) {
    // Map section selectors to their required scripts
    const sectionScriptMap = {
      '.product-form': ['product-form.js', 'pickup-availability.js'],
      '.cart-drawer': ['cart-drawer.js', 'cart-notification.js'],
      '.facets-container': ['facets.js'],
      'predictive-search': ['predictive-search.js'],
      '.collection-list': ['collection-list.js'],
      '.featured-collection': ['animations.js', 'product-slider.js']
    };
    
    // Create observer instance with generous threshold
    const scriptObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target;
          
          // Find matching scripts to load
          Object.entries(sectionScriptMap).forEach(([selector, scripts]) => {
            if (section.matches(selector) || section.querySelector(selector)) {
              // Use requestIdleCallback to load scripts during browser idle time
              const loadScripts = () => {
                scripts.forEach(script => {
                  // Check if script is already loaded
                  const scriptId = `deferred-${script.replace('.js', '')}`;
                  if (!document.getElementById(scriptId)) {
                    const scriptElement = document.createElement('script');
                    scriptElement.id = scriptId;
                    scriptElement.src = `${window.Shopify?.routes?.root || '/'}cdn/shop/t/168/assets/${script}`;
                    scriptElement.defer = true;
                    document.body.appendChild(scriptElement);
                    perfMetrics.resourcesDeferred++;
                    
                    scriptElement.onload = () => {
                      perfMetrics.resourcesLoaded++;
                      // Dispatch event to notify script load completion
                      window.dispatchEvent(new CustomEvent('deferred-script-loaded', {
                        detail: { scriptName: script }
                      }));
                    };
                  }
                });
              };
              
              if ('requestIdleCallback' in window) {
                requestIdleCallback(loadScripts);
              } else {
                setTimeout(loadScripts, 10);
              }
              
              // Stop observing this section once we've identified scripts to load
              scriptObserver.unobserve(section);
            }
          });
        }
      });
    }, {
      rootMargin: '300px', // Very generous margin to start loading well before visible
      threshold: 0.01 // Trigger with just 1% visibility
    });
    
    // Start observing sections
    Object.keys(sectionScriptMap).forEach(selector => {
      document.querySelectorAll(selector).forEach(section => {
        scriptObserver.observe(section);
      });
    });
  }
}

// Aggressively control third-party scripts
function optimizeThirdPartyScripts() {
  // List of third-party scripts to control, with priority levels
  const thirdPartyScripts = [
    // High-impact analytics (delay significantly)
    { 
      src: 'https://www.googletagmanager.com/gtag/js?id=G-QE3J2XV888',
      id: 'google-analytics',
      priority: 'low',
      dataset: {},
      attributes: { async: true },
      delay: 3000
    },
    { 
      src: 'https://www.googletagmanager.com/gtag/js?id=AW-1018657019',
      id: 'google-ads',
      priority: 'low',
      dataset: {},
      attributes: { async: true },
      delay: 3000
    },
    // Judge.me (can be delayed)
    {
      src: 'https://cdn1.judge.me/assets/installed.js', 
      id: 'judgeme-script',
      priority: 'medium',
      dataset: { shop: 'original-mattress-company-australia.myshopify.com' },
      attributes: { async: true },
      delay: 2000
    },
    // Shopify client (lower impact, but still delay)
    { 
      src: 'https://cdn.shopify.com/shopifycloud/shop-js/v1.0/client.js', 
      id: 'shopify-features',
      priority: 'medium', 
      dataset: {},
      attributes: { async: true },
      delay: 1500
    }
  ];
  
  // Schedule loading of third-party scripts based on user interaction and priority
  let hasUserInteracted = false;
  
  // Detect any user interaction
  const userInteractionHandler = () => {
    hasUserInteracted = true;
    
    // Load medium priority scripts after interaction
    thirdPartyScripts
      .filter(script => script.priority === 'medium')
      .forEach(script => loadThirdPartyScript(script));
      
    // Remove event listeners once handled
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
      window.removeEventListener(event, userInteractionHandler, { passive: true });
    });
  };
  
  // Add event listeners for interaction detection
  ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
    window.removeEventListener(event, userInteractionHandler);
    window.addEventListener(event, userInteractionHandler, { passive: true });
  });
  
  // Helper function to load a third-party script
  const loadThirdPartyScript = (scriptInfo) => {
    if (!document.getElementById(scriptInfo.id)) {
      const script = document.createElement('script');
      script.src = scriptInfo.src;
      script.id = scriptInfo.id;
      
      // Apply any attributes
      if (scriptInfo.attributes) {
        Object.entries(scriptInfo.attributes).forEach(([key, value]) => {
          script.setAttribute(key, value);
        });
      }
      
      // Add data attributes
      if (scriptInfo.dataset) {
        Object.entries(scriptInfo.dataset).forEach(([key, value]) => {
          script.dataset[key] = value;
        });
      }
      
      // Track loading
      perfMetrics.thirdPartyScriptsDeferred++;
      document.body.appendChild(script);
    }
  };
  
  // Schedule script loading
  // 1. Immediate: None - all third party scripts are delayed
  // 2. On interaction: Medium priority 
  // 3. Delayed: Low priority
  setTimeout(() => {
    // Load low priority scripts after significant delay
    thirdPartyScripts
      .filter(script => script.priority === 'low')
      .forEach(script => {
        setTimeout(() => loadThirdPartyScript(script), script.delay);
      });
      
    // Load medium priority if user hasn't interacted yet
    if (!hasUserInteracted) {
      setTimeout(() => {
        thirdPartyScripts
          .filter(script => script.priority === 'medium' && !document.getElementById(script.id))
          .forEach(script => loadThirdPartyScript(script));
      }, 2500);
    }
  }, 1500);
}

// Add passive event listeners for better performance
function optimizeEventListeners() {
  const passiveEvents = ['touchstart', 'touchmove', 'wheel', 'mousewheel', 'scroll'];
  
  // Detect passive listener support
  let supportsPassive = false;
  try {
    const opts = Object.defineProperty({}, 'passive', {
      get: function() {
        supportsPassive = true;
        return true;
      }
    });
    window.addEventListener('testpassive', null, opts);
    window.removeEventListener('testpassive', null, opts);
  } catch (e) {}
  
  // Override addEventListener to use passive option for relevant events
  if (supportsPassive) {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function(type, listener, options) {
      if (passiveEvents.includes(type)) {
        const passiveOptions = 
          typeof options === 'object' ? { ...options, passive: true } : 
          options === undefined ? { passive: true } : options;
        
        originalAddEventListener.call(this, type, listener, passiveOptions);
      } else {
        originalAddEventListener.call(this, type, listener, options);
      }
    };
  }
}

// Block render-blocking Google Tag Manager requests
function blockGTMMainThreadImpact() {
  // Create a clean performance observer to monitor long tasks
  if ('PerformanceObserver' in window) {
    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        
        // Analyze long tasks
        entries.forEach(entry => {
          // If we have attribution data, check if it's from GTM
          if (entry.attribution && entry.attribution.length > 0) {
            const isGTMTask = entry.attribution.some(attr => 
              attr.containerType === 'script' && 
              attr.containerName && 
              (attr.containerName.includes('googletagmanager') || 
               attr.containerName.includes('gtag') ||
               attr.containerName.includes('google-analytics'))
            );
            
            if (isGTMTask) {
              perfMetrics.mainThreadTimeBlocked += entry.duration;
              console.log('GTM blocked main thread for', entry.duration, 'ms');
              
              // If GTM is significantly impacting performance, take additional measures
              if (perfMetrics.mainThreadTimeBlocked > 100) {
                // Remove any remaining googletagmanager scripts that haven't loaded yet
                document.querySelectorAll('script[src*="googletagmanager.com"]:not([loaded="true"])').forEach(script => {
                  script.dataset.deferred = 'true';
                  if (script.parentNode) {
                    script.parentNode.removeChild(script);
                  }
                });
              }
            }
          }
        });
      });
      
      // Start observing long tasks
      longTaskObserver.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      console.log('PerformanceObserver for longtask not supported');
    }
  }
}

// Execute optimizations based on page stage
function initializeOptimizations() {
  // Add preconnects immediately
  addPreconnects();
  
  // First wave - critical optimizations (passive listeners)
  optimizeEventListeners();
  
  // Load non-critical styles with slight delay
  setTimeout(loadDeferredStyles, 0);
  
  // Setup intersection observers 
  setupIntersectionObserver();
  
  // Block GTM impact on main thread
  blockGTMMainThreadImpact();
  
  // Defer third-party script loading to last
  setTimeout(optimizeThirdPartyScripts, 200);
  
  // Debug information
  if (window.location.search.includes('debug_performance=1')) {
    console.log('[Performance] Advanced deferred loading initialized');
    
    // Log metrics periodically
    const logInterval = setInterval(() => {
      console.log('[Performance] Metrics:', perfMetrics);
      
      // Stop logging after 15 seconds
      if (document.readyState === 'complete' && 
          performance.now() > 15000) {
        clearInterval(logInterval);
      }
    }, 3000);
  }
}

// Initialize with appropriate timing
document.addEventListener('DOMContentLoaded', initializeOptimizations); 